import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import { AdminFormField } from '@default-admin/admin-form/admin-form.component';
import { FlamingoHttpService } from '@flamingo/service/flamingo-http.service';
import { formatDate } from 'date-fns';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
  NamespaceParamModel,
  NamespaceParamTypes,
} from '../../projects/fs-bilanci-app/src/app/children/main/children/namespace-param/namespace-param.model';
import { NamespaceModel } from '../model/namespace.model';

@Injectable({
  providedIn: 'root',
})
export class GlobalNamespaceService {
  private _namespaces$: BehaviorSubject<NamespaceModel[]> = new BehaviorSubject<NamespaceModel[]>([]);
  public namespaces$: Observable<NamespaceModel[]> = this._namespaces$.asObservable();
  private _additionalNamespaces$: BehaviorSubject<NamespaceModel[]> = new BehaviorSubject<NamespaceModel[]>([]);
  public additionalNamespaces$: Observable<NamespaceModel[]> = this._additionalNamespaces$.asObservable();
  private _namespaceParams$: BehaviorSubject<NamespaceParamModel[]> = new BehaviorSubject<NamespaceParamModel[]>([]);
  public namespaceParams$: Observable<NamespaceParamModel[]> = this._namespaceParams$.asObservable();

  constructor(
    private http: FlamingoHttpService,
  ) {
  }

  subscribeToNamespace(namespace: string, duration: string | null): Observable<NamespaceModel> {
    return this.http.put(`/namespaces/${namespace}/subscribe`, { duration });
  }

  get namespaces(): NamespaceModel[] {
    return this._namespaces$.value;
  }

  get additionalNamespaces(): NamespaceModel[] {
    return this._additionalNamespaces$.value;
  }

  get namespaceParams(): NamespaceParamModel[] {
    return this._namespaceParams$.value;
  }

  getNamespaceConfiguration(namespace: string): NamespaceModel | undefined {
    return this.namespaces.find(n => n.namespace === namespace);
  }

  public fetch(): Observable<NamespaceModel[]> {
    return this.list()
      .pipe(
        switchMap(items => this.listParams()
          .pipe(
            tap(params => this._namespaceParams$.next(params)),
            map(_ => items),
          )),
        switchMap(items => this.listAdditional()
          .pipe(
            tap(namespaces => this._additionalNamespaces$.next(namespaces)),
            map(_ => items),
          )),
        tap(items => this._namespaces$.next(items.sort((n1, n2) => n1.description.toLowerCase() > n2.description.toLowerCase() ? 1 : -1))),
      );
  }

  public buildKeyFields(namespace: NamespaceModel, values?: { key1?: string; key2?: string; key3?: string }): AdminFormField[] {
    const keyParams: NamespaceParamModel[] = ([1, 2, 3] as const)
      .filter(n => !!namespace[`key${n}Description`])
      .map(n => ({
        id: `key${n}`,
        key: `key${n}`,
        type: (namespace[`key${n}Type`] as NamespaceParamTypes) || NamespaceParamTypes.STRING,
        description: namespace[`key${n}Description`]!,
        hint: namespace[`key${n}Hint`],
        mandatory: n === 1
      }));
    return this.buildFields(keyParams, values, {}, '');
  }

  public buildFields(parameters: NamespaceParamModel[], values?: any, config?: Partial<AdminFormField>, namePrefix = '_p_'): AdminFormField[] {
    const fields: AdminFormField[] = [];

    parameters.forEach(p => {
      const base = {
        name: `${namePrefix}${p.key}`,
        value: values?.[p.key],
        label: p.description,
        hint: p.hint,
        validators: [
          ...(p.mandatory ? [Validators.required] : []),
        ],
        errors: [
          { key: 'required', value: 'Campo obbligatorio' },
        ],
        ...config,
      };

      switch (p.type) {
        case NamespaceParamTypes.DATE:
          fields.push({ ...base, type: 'datepicker', transform: (input: Date) => formatDate(input, 'yyyy-MM-dd')});
          break;
        case NamespaceParamTypes.YEAR:
          fields.push({ ...base, type: 'input', option: 'number' });
          break;
        default:
          fields.push({ ...base, type: 'input', option: 'text' });
      }
    });

    return fields;
  }

  private list(): Observable<NamespaceModel[]> {
    return this.http.get<NamespaceModel[]>(`/namespaces`);
  }

  private listAdditional(): Observable<NamespaceModel[]> {
    return this.http.get<NamespaceModel[]>(`/documents/my-documents/namespaces`)
      .pipe(
        catchError(_ => of([])),
      );
  }

  private listParams(): Observable<NamespaceParamModel[]> {
    return this.http.get<NamespaceParamModel[]>(`/parameters`);
  }
}
